import React, { useState } from 'react';
import { IntelligenceShell } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ResponsiveContainer, BarChart, Bar, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

export default function DemandCalendarReport() {
  const [months, setMonths] = useState(12);
  const { data, loading } = useIntelligenceReport('demand-calendar', months);
  const { formatMoney } = useOperatorLocale();
  const s = data?.summary;
  const maxDow = Math.max(1, ...(data?.dayOfWeek || []).map((d) => d.bookings));
  const monthly = (data?.monthly || []).map((m) => ({ ...m, month: m.month.slice(5) + '/' + m.month.slice(2, 4) }));

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['DEMAND CALENDAR', `${data.periodStart} to ${data.periodEnd}`],
      [],
      ['Day of week', 'Trips', 'Revenue'],
      ...data.dayOfWeek.map((d) => [d.day, d.bookings, d.revenue]),
      [],
      ['Start time', 'Trips'],
      ...data.hours.map((h) => [h.hour, h.bookings]),
      [],
      ['Booked ahead', 'Bookings'],
      ...data.leadTime.map((l) => [l.bucket, l.bookings]),
      [],
      ['Month', 'Trips', 'Revenue'],
      ...data.monthly.map((m) => [m.month, m.bookings, m.revenue]),
    ], `demand-calendar-${data.periodStart}-to-${data.periodEnd}.csv`);
  };

  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: `demand-calendar-${data.periodStart}-to-${data.periodEnd}.pdf`,
      title: 'Demand Center',
      subtitle: `Your bookings only · ${data.periodStart} to ${data.periodEnd}`,
      kpis: [
        ['Trips in period', String(s.totalBookings)],
        ['Busiest day', s.busiestDay ?? '-'],
        ['Peak start time', s.peakHour ?? '-'],
        ['Median lead time', s.medianLeadDays != null ? `${s.medianLeadDays} days` : '-'],
        ['Weekend share', `${s.weekendShare}%`],
      ],
      tables: [
        { title: 'Trips by day of week', head: ['Day', 'Trips', 'Revenue'], rows: data.dayOfWeek.map((d) => [d.day, d.bookings, formatMoney(d.revenue)]) },
        { title: 'Trips by start time', head: ['Start time', 'Trips'], rows: data.hours.map((h) => [h.hour, h.bookings]) },
        { title: 'How far ahead customers book', head: ['Booked ahead', 'Bookings'], rows: data.leadTime.map((l) => [l.bucket, l.bookings]) },
        { title: 'Monthly demand', head: ['Month', 'Trips', 'Revenue'], rows: data.monthly.map((m) => [m.month, m.bookings, formatMoney(m.revenue)]) },
      ],
    });
  };

  return (
    <IntelligenceShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="demand-calendar-report">
        <ReportHeader
          title="Demand Center"
          blurb={`When your customers book and when they show up over the last ${months} months — by day of week, start time, and how far in advance they commit.`}
          right={<><AiSuggestion report="demand-calendar" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="demand" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="demand-export" /></>}
        />

        {loading ? <ReportSkeleton /> : !data ? null : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <KpiCard delay={60} testid="kpi-demand-busiest" label="Busiest day" value={s.busiestDay ?? '—'} sub={`${s.weekendShare}% of trips fall on weekends`} />
              <KpiCard delay={120} testid="kpi-demand-peak" label="Peak start time" value={s.peakHour ?? '—'} sub="most common departure" />
              <KpiCard delay={180} testid="kpi-demand-lead" label="Median lead time" value={s.medianLeadDays != null ? `${s.medianLeadDays} days` : '—'} sub="from booking to trip" />
              <KpiCard delay={240} testid="kpi-demand-total" label="Trips in period" value={s.totalBookings.toLocaleString()} sub={`last ${data.periodMonths} months`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
              <ChartCard delay={300} title="Trips by day of week" testid="demand-dow-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.dayOfWeek} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v, n, p) => [`${v} trips · ${formatMoney(p.payload.revenue)}`, p.payload.day]} contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Bar dataKey="bookings" radius={[8, 8, 0, 0]} barSize={28}>
                        {data.dayOfWeek.map((d, i) => (
                          <Cell key={i} fill={d.bookings === maxDow ? '#2563EB' : '#BFDBFE'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard delay={360} title="Trips by start time" testid="demand-hours-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.hours} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v) => [`${v} trips`, 'Departures']} contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Bar dataKey="bookings" fill="#60A5FA" radius={[8, 8, 0, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard delay={420} title="How far ahead customers book" testid="demand-lead-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.leadTime} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v) => [`${v} bookings`, 'Booked ahead']} contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Bar dataKey="bookings" fill="#3B82F6" radius={[8, 8, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard delay={480} title="Monthly demand" testid="demand-monthly-chart">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="demandFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2563EB" stopOpacity={0.16} />
                          <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v, n, p) => [`${v} trips · ${formatMoney(p.payload.revenue)}`, p.payload.month]} contentStyle={tooltipStyle} />
                      <Area type="monotone" dataKey="bookings" stroke="#2563EB" strokeWidth={2} fill="url(#demandFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
          </>
        )}
      </main>
    </IntelligenceShell>
  );
}
